import { ControlCodes } from '@bcube/shared/constants';
import {
  Activity,
  BadgeCheck,
  BookOpen,
  Building2,
  CircuitBoard,
  CreditCard,
  Download,
  Factory,
  FileCheck,
  FileText,
  HardDrive,
  Home,
  List,
  Package,
  RefreshCcw,
  ScrollText,
  Settings,
  Sheet,
  Shield,
  ShoppingCart,
  TableProperties,
  Terminal,
  TestTube,
  Users,
  Wifi,
  Wrench,
} from 'lucide-react';
import { adminModule } from './admin';
import { commercialModule } from './commercial';
import { connectivityModule } from './connectivity';
import { productionModule } from './production';
import type { ModuleConfig, ModuleNavItem } from './types';

export * from './types';
export { adminModule, commercialModule, productionModule, connectivityModule };

// All modules in a map for easy lookup
export const modules: Record<string, ModuleConfig> = {
  admin: adminModule,
  commercial: commercialModule,
  production: productionModule,
  connectivity: connectivityModule,
};

// Unified tree navigation (like IDE folder structure)
export const unifiedNavigation: ModuleNavItem[] = [
  {
    name: 'Home',
    pathname: '/home',
    icon: <Home className="h-5 w-5" />,
  },
  {
    name: 'Admin',
    pathname: '/admin',
    icon: <Users className="h-5 w-5" />,
    children: [
      {
        name: 'Usuários',
        pathname: '/admin/users',
        icon: <Users className="h-5 w-5 shrink-0" />,
        code: ControlCodes.admin_user,
      },
      {
        name: 'Perfis',
        pathname: '/admin/profiles',
        icon: <BookOpen className="h-5 w-5 shrink-0" />,
        code: ControlCodes.admin_profile_view,
      },
      {
        name: 'Controle de acesso',
        pathname: '/admin/control',
        icon: <Shield className="h-5 w-5 shrink-0" />,
        code: ControlCodes.admin_control,
      },
    ],
  },
  {
    name: 'Comercial',
    pathname: '/commercial',
    icon: <ShoppingCart className="h-5 w-5" />,
    children: [
      {
        name: 'Produtos',
        pathname: '/commercial/products',
        icon: <Package className="h-5 w-5 shrink-0" />,
        code: ControlCodes.commercial_product,
      },
      {
        name: 'Contas',
        pathname: '/commercial/accounts',
        icon: <Building2 className="h-5 w-5 shrink-0" />,
        code: ControlCodes.commercial_accounts,
      },
      {
        name: 'Propostas',
        pathname: '/commercial/proposals',
        icon: <FileText className="h-5 w-5 shrink-0" />,
        code: ControlCodes.commercial_proposal,
      },
      {
        name: 'Tabela de Preços',
        pathname: '/commercial/price-tables',
        icon: <TableProperties className="h-5 w-5 shrink-0" />,
        code: ControlCodes.commercial_price_table,
      },
      {
        name: 'Análise de Crédito',
        pathname: '/commercial/credit-analysis',
        icon: <CreditCard className="h-5 w-5 shrink-0" />,
        code: ControlCodes.commercial_credit_analysis,
      },
    ],
  },
  {
    name: 'Produção',
    pathname: '/production',
    icon: <Factory className="h-5 w-5" />,
    children: [
      {
        name: 'Dispositivos',
        pathname: '/production/devices',
        icon: <HardDrive className="h-5 w-5 shrink-0" />,
        code: ControlCodes.production_device,
      },
      {
        name: 'Perfis de Configuração',
        pathname: '/production/config-profiles',
        icon: <Settings className="h-5 w-5 shrink-0" />,
        code: ControlCodes.production_configuration_profile,
      },
      {
        name: 'Ferramentas',
        pathname: '/production/tools',
        icon: <Wrench className="h-5 w-5 shrink-0" />,
        code: ControlCodes.production_tool,
        children: [
          {
            name: 'Auto Teste',
            pathname: '/production/tools/auto-test',
            icon: <TestTube className="h-5 w-5 shrink-0" />,
            code: ControlCodes.production_tool,
          },
          {
            name: 'Configurador',
            pathname: '/production/tools/configurator',
            icon: <Settings className="h-5 w-5 shrink-0" />,
            code: ControlCodes.production_tool,
          },
          {
            name: 'Gravação de Firmware',
            pathname: '/production/tools/firmware-update',
            icon: <Download className="h-5 w-5 shrink-0" />,
            code: ControlCodes.production_tool,
          },
          {
            name: 'Identificador',
            pathname: '/production/tools/identificator',
            icon: <CircuitBoard className="h-5 w-5 shrink-0" />,
            code: ControlCodes.production_tool,
          },
          {
            name: 'Check de Configuração',
            pathname: '/production/tools/check-configuration',
            icon: <FileCheck className="h-5 w-5 shrink-0" />,
            code: ControlCodes.production_tool,
          },
          {
            name: 'Terminal Serial',
            pathname: '/production/tools/commands',
            icon: <Terminal className="h-5 w-5 shrink-0" />,
            code: ControlCodes.production_tool,
          },
        ],
      },
      {
        name: 'Logs',
        pathname: '/production/logs',
        icon: <ScrollText className="h-5 w-5 shrink-0" />,
        code: ControlCodes.production_logs,
        children: [
          {
            name: 'Auto Teste',
            pathname: '/production/logs/auto-test',
            icon: <TestTube className="h-5 w-5 shrink-0" />,
            code: ControlCodes.production_logs,
          },
          {
            name: 'Configuração',
            pathname: '/production/logs/configurator',
            icon: <Wrench className="h-5 w-5 shrink-0" />,
            code: ControlCodes.production_logs,
          },
          {
            name: 'Gravação de Firmware',
            pathname: '/production/logs/firmware-update',
            icon: <Download className="h-5 w-5 shrink-0" />,
            code: ControlCodes.production_logs,
          },
          {
            name: 'Identificação',
            pathname: '/production/logs/identificator',
            icon: <BadgeCheck className="h-5 w-5 shrink-0" />,
            code: ControlCodes.production_logs,
          },
        ],
      },
    ],
  },
  {
    name: 'Conectividade',
    pathname: '/connectivity',
    icon: <Wifi className="h-5 w-5" />,
    children: [
      {
        name: 'Sincronização KORA',
        pathname: '/connectivity/kora-sync',
        icon: <RefreshCcw className="h-5 w-5 shrink-0" />,
        code: ControlCodes.connectivity_kora_sync,
      },
      {
        name: 'Ativação LoRaWan',
        pathname: '/connectivity/lora-activation',
        icon: <Activity className="h-5 w-5 shrink-0" />,
        code: ControlCodes.connectivity_lora_activation,
        children: [
          {
            name: 'Logs de ativação',
            pathname: '/connectivity/lora-activation-logs',
            icon: <List className="h-5 w-5 shrink-0" />,
            code: ControlCodes.connectivity_lora_activation,
          },
          {
            name: 'Ações em massa',
            pathname: '/connectivity/batch-lora-activation',
            icon: <Sheet className="h-5 w-5 shrink-0" />,
            code: ControlCodes.connectivity_lora_activation,
          },
        ],
      },
    ],
  },
];

// Get module by path
export function getModuleByPath(path: string): ModuleConfig | undefined {
  const modulePath = `/${path.split('/')[1]}`; // Get first segment
  return Object.values(modules).find((m) => m.basePath === modulePath);
}
